// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Analytics Page
// ═══════════════════════════════════════════════════════════

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { formatDuration } from '@/lib/utils';
import { Card, PageHeader, StatCard, ProgressBar, Button } from '@/components/ui';
import {
  ArrowLeft,
  TrendingUp,
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = parseInt(id!);

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });

  const { data: trendData } = useQuery({
    queryKey: ['analytics-trends', projectId],
    queryFn: () => api.getTrends(projectId, 30),
  });

  const { data: flakyData } = useQuery({
    queryKey: ['analytics-flaky', projectId],
    queryFn: () => api.getFlakyTests(projectId),
  });

  const { data: coverageData } = useQuery({
    queryKey: ['analytics-coverage', projectId],
    queryFn: () => api.getCoverage(projectId),
  });

  const summary = trendData?.summary;
  const trends = trendData?.trends || [];
  const flaky = flakyData?.flakyTests || [];
  const coverage = coverageData?.coverage;

  const chartData = trends
    .map((t, i) => ({
      name: `Run ${i + 1}`,
      passed: t.passedTests || 0,
      failed: t.failedTests || 0,
      duration: t.durationMs ? Math.round(t.durationMs / 1000) : 0,
    }))
    .reverse();

  const coverageChartData = coverage
    ? Object.entries(coverage.byType).map(([type, count]) => ({ name: type, tests: count }))
    : [];

  return (
    <div className="p-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/projects/${projectId}`)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.backTo', { name: project?.projectName || t('nav.projects') })}
      </Button>

      <PageHeader
        title={t('analytics.title')}
        subtitle={t('analytics.subtitle', { name: project?.projectName || 'project' })}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('analytics.totalRuns')}
          value={summary?.totalRuns || 0}
          icon={<Activity className="w-6 h-6" />}
          color="brand"
        />
        <StatCard
          label={t('analytics.passedRuns')}
          value={summary?.passedRuns || 0}
          icon={<CheckCircle2 className="w-6 h-6" />}
          color="success"
        />
        <StatCard
          label={t('analytics.failedRuns')}
          value={summary?.failedRuns || 0}
          icon={<XCircle className="w-6 h-6" />}
          color="danger"
        />
        <StatCard
          label={t('analytics.avgDuration')}
          value={summary?.avgDuration ? formatDuration(summary.avgDuration) : '—'}
          icon={<Clock className="w-6 h-6" />}
          color="warning"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        {/* Pass/Fail Trend Chart */}
        <Card>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-brand-400" />
            {t('analytics.passFailTrend')}
          </h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="passGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(160 84% 39%)" stopOpacity={0.3} />
                    {/* #10b981 */}
                    <stop offset="95%" stopColor="hsl(160 84% 39%)" stopOpacity={0} />
                    {/* #10b981 */}
                  </linearGradient>
                  <linearGradient id="failGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(0 84% 60%)" stopOpacity={0.3} />
                    {/* #ef4444 */}
                    <stop offset="95%" stopColor="hsl(0 84% 60%)" stopOpacity={0} />
                    {/* #ef4444 */}
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="passed"
                  stroke="hsl(160 84% 39%)"
                  fill="url(#passGrad)"
                  strokeWidth={2}
                />
                {/* #10b981 */}
                <Area
                  type="monotone"
                  dataKey="failed"
                  stroke="hsl(0 84% 60%)"
                  fill="url(#failGrad)"
                  strokeWidth={2}
                />
                {/* #ef4444 */}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-surface-500 text-center py-20">
              {t('analytics.noTrendData')}
            </p>
          )}
        </Card>

        {/* Coverage Chart */}
        <Card>
          <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-brand-400" />
            {t('analytics.coverageByType')}
          </h3>
          {coverageChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={coverageChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="tests" fill="hsl(228 100% 60%)" radius={[4, 4, 0, 0]} />
                {/* #3668ff */}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-surface-500 text-center py-20">
              {t('analytics.noCoverageData')}
            </p>
          )}
        </Card>
      </div>

      {/* Flaky Tests */}
      <Card>
        <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-warning" />
          {t('analytics.flakyDetection')}
        </h3>
        {flaky.length > 0 ? (
          <div className="space-y-2">
            {flaky.map((test, i) => (
              <motion.div
                key={test.testName}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center justify-between p-3 rounded-lg bg-surface-800/30"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm text-surface-200 font-mono truncate">
                    {test.testName}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-xs">
                    <span className="text-success">
                      {t('analytics.passedLabel', { count: test.passCount })}
                    </span>
                    <span className="text-surface-600 mx-1">/</span>
                    <span className="text-danger">
                      {t('analytics.failedLabel', { count: test.failCount })}
                    </span>
                  </div>
                  <div className="w-28">
                    <ProgressBar
                      value={test.flakyRate}
                      max={100}
                      color={
                        test.flakyRate > 50 ? 'danger' : test.flakyRate > 25 ? 'warning' : 'brand'
                      }
                      showLabel
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-surface-500 text-center py-8">{t('analytics.noFlakyTests')}</p>
        )}
      </Card>
    </div>
  );
}
